import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Loading from '../common/Loading';
import AcControlPanel from './AcControlPanel';
import SchedulePeriodsManager from '../common/SchedulePeriodsManager';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../utils/constants';

const AssociationDetails = ({ association, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(false);
  const [priorityUpdating, setPriorityUpdating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    autoMode: true,
    presenceDelay: 30,
    minOnDuration: 60,
  });

  useEffect(() => {
    fetchDetails();
  }, [association._id]);

  const fetchDetails = async () => {
    try {
      const response = await api.get(`/associations/${association._id}`);
      setDetails(response.data.data);
      
      setFormData({
        name: response.data.data.name,
        description: response.data.data.description || '',
        autoMode: response.data.data.autoMode,
        presenceDelay: response.data.data.settings.presenceDelay,
        minOnDuration: response.data.data.settings.minOnDuration,
      });
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOUVEAU — changer le device prioritaire ON/OFF (Admin uniquement, appliqué
  // immédiatement, indépendant du bouton "Enregistrer" du reste du formulaire)
  const handleUpdatePriority = async (controlPriority) => {
    if (controlPriority === details.controlPriority) return;
    setPriorityUpdating(true);
    try {
      await api.patch(`/associations/${association._id}/control-priority`, { controlPriority });
      await fetchDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors du changement de priorité');
    } finally {
      setPriorityUpdating(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await api.put(`/associations/${association._id}`, {
        name: formData.name,
        description: formData.description,
        autoMode: formData.autoMode,
        settings: {
          presenceDelay: formData.presenceDelay,
          minOnDuration: formData.minOnDuration,
        },
      });
      
      alert('Association mise à jour avec succès');
      setEditing(false);
      fetchDetails();
      onUpdate();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette association ?')) {
      return;
    }

    try {
      await api.delete(`/associations/${association._id}`);
      alert('Association supprimée avec succès');
      onUpdate();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleControl = async (action) => {
    try {
      await api.post(`/associations/${association._id}/control`, { action });
      alert(`Switch ${action === 'on' ? 'allumé' : 'éteint'} avec succès`);
      fetchDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors du contrôle');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <button
            onClick={onClose}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Retour
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{details.name}</h1>
          <p className="text-gray-600 mt-1">{details.description}</p>
        </div>
        <div className="flex space-x-3">
          {!editing ? (
            <>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                ✏️ Modifier
              </Button>
              {user?.role === ROLES.ADMIN && (
                <Button variant="danger" onClick={handleDelete}>
                  🗑️ Supprimer
                </Button>
              )}
            </>
          ) : (
            <Button variant="secondary" onClick={() => setEditing(false)}>
              ✖️ Annuler
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne 1 : Informations */}
        <div className="lg:col-span-2 space-y-6">
          {editing ? (
            <Card>
              <h2 className="text-xl font-bold mb-4">Modifier l'association</h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <Input
                  label="Nom"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                    rows="3"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.autoMode}
                    onChange={(e) => setFormData({ ...formData, autoMode: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Mode automatique activé
                  </label>
                </div>

                <SchedulePeriodsManager
                  scheduleEnabled={details.schedule?.enabled || false}
                  onToggleEnabled={async (enabled) => {
                    await api.put(`/associations/${association._id}`, { schedule: { enabled } });
                    await fetchDetails();
                  }}
                  periods={details.schedulePeriods || []}
                  legacyOnTime={details.schedule?.onTime}
                  legacyOffTime={details.schedule?.offTime}
                  periodsBasePath={`/associations/${association._id}/schedule-periods`}
                  onPeriodsChange={() => fetchDetails()}
                />

                <div className="border-t pt-4">
                  <h3 className="font-bold mb-3">Paramètres avancés</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Délai avant extinction (secondes)
                      </label>
                      <input
                        type="number"
                        value={formData.presenceDelay}
                        onChange={(e) => setFormData({ ...formData, presenceDelay: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Durée minimale ON (secondes)
                      </label>
                      <input
                        type="number"
                        value={formData.minOnDuration}
                        onChange={(e) => setFormData({ ...formData, minOnDuration: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button type="submit" className="flex-1">
                    💾 Enregistrer
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(false)} className="flex-1">
                    Annuler
                  </Button>
                </div>
              </form>
            </Card>
          ) : (
            <>
              {/* Devices */}
              <Card>
                <h2 className="text-xl font-bold mb-4">Devices associés</h2>
                
                <div className="space-y-4">
                  {/* Sensor */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">📡</span>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold">Capteur</p>
                          <p className="font-bold text-gray-800">{details.sensorId?.name}</p>
                          <p className="text-xs text-gray-500">{details.sensorId?.deviceId}</p>
                        </div>
                      </div>
                      {details.sensorId?.online ? (
                        <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">
                          En ligne
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">
                          Hors ligne
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Switch */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">{details.switchId?.type === 'ir_controller' ? '🌡️' : '💡'}</span>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold">
                            {details.switchId?.type === 'ir_controller' ? 'Contrôleur IR' : 'Interrupteur'}
                          </p>
                          <p className="font-bold text-gray-800">{details.switchId?.name}</p>
                          <p className="text-xs text-gray-500">{details.switchId?.deviceId}</p>
                        </div>
                      </div>
                      {details.switchId?.online ? (
                        <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">
                          En ligne
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">
                          Hors ligne
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ✅ NOUVEAU — 3e device (Wifi Smart Meter Pro), uniquement si présent */}
                  {details.meterId && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-3xl">⚡</span>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-semibold">Smart Meter Pro</p>
                            <p className="font-bold text-gray-800">{details.meterId.name}</p>
                            <p className="text-xs text-gray-500">{details.meterId.deviceId}</p>
                          </div>
                        </div>
                        {details.meterId.online ? (
                          <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">En ligne</span>
                        ) : (
                          <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">Hors ligne</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* ✅ NOUVEAU — Priorité ON/OFF, uniquement pour une association à 3 devices.
                  Modifiable par l'Admin uniquement — l'Owner la voit en lecture seule. */}
              {details.meterId && (
                <Card>
                  <h2 className="text-xl font-bold mb-1">Priorité ON/OFF</h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Un seul device reçoit les commandes ON/OFF automatiques et manuelles.
                  </p>
                  {user?.role === ROLES.ADMIN ? (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleUpdatePriority('IR_L5')}
                        disabled={priorityUpdating}
                        className={`text-left px-4 py-3 rounded-lg border-2 transition disabled:opacity-50 ${
                          details.controlPriority === 'IR_L5' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-800">🌡️ Contrôleur IR L5</p>
                        <p className="text-xs text-gray-500 mt-0.5">Recommandé</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdatePriority('SMART_METER_PRO')}
                        disabled={priorityUpdating}
                        className={`text-left px-4 py-3 rounded-lg border-2 transition disabled:opacity-50 ${
                          details.controlPriority === 'SMART_METER_PRO' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-800">⚡ Wifi Smart Meter Pro</p>
                        <p className="text-xs text-gray-500 mt-0.5">Coupe l'alimentation directement</p>
                      </button>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                      <span className="text-lg">{details.controlPriority === 'SMART_METER_PRO' ? '⚡' : '🌡️'}</span>
                      <span className="font-medium text-gray-800">
                        {details.controlPriority === 'SMART_METER_PRO' ? 'Wifi Smart Meter Pro' : 'Contrôleur IR L5'}
                      </span>
                      <span className="text-xs text-gray-400">(défini par l'Admin)</span>
                    </div>
                  )}
                </Card>
              )}

              {/* Statut temps réel */}
              <Card>
                <h2 className="text-xl font-bold mb-4">Statut temps réel</h2>
                
                <div className="space-y-4">
                  {/* Sensor Status */}
                  <div>
                    <h3 className="font-semibold mb-2">📡 Capteur</h3>
                    {details.realtimeStatus?.sensor && details.realtimeStatus.sensor.length > 0 ? (
                      <div className="bg-gray-50 rounded-lg p-3">
                        {details.realtimeStatus.sensor.map((status, index) => (
                          <div key={index} className="flex justify-between text-sm py-1">
                            <span className="text-gray-600">{status.code}:</span>
                            <span className="font-mono font-bold">
                              {typeof status.value === 'boolean' 
                                ? (status.value ? 'true' : 'false')
                                : status.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune donnée disponible</p>
                    )}
                  </div>

                  {/* Switch Status */}
                  <div>
                    <h3 className="font-semibold mb-2">
                      {details.switchId?.type === 'ir_controller' ? '🌡️ Climatisation' : '💡 Interrupteur'}
                    </h3>
                    {details.switchId?.type === 'ir_controller' ? (
                      details.realtimeStatus?.switch ? (
                        <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">power:</span>
                            <span className="font-mono font-bold">
                              {details.realtimeStatus.switch.power ? 'ON' : 'OFF'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">temp:</span>
                            <span className="font-mono font-bold">{details.realtimeStatus.switch.temp}°C</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">mode:</span>
                            <span className="font-mono font-bold">{details.realtimeStatus.switch.mode}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">wind:</span>
                            <span className="font-mono font-bold">{details.realtimeStatus.switch.wind}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">Aucune donnée disponible</p>
                      )
                    ) : details.realtimeStatus?.switch && details.realtimeStatus.switch.length > 0 ? (
                      <div className="bg-gray-50 rounded-lg p-3">
                        {details.realtimeStatus.switch.map((status, index) => (
                          <div key={index} className="flex justify-between text-sm py-1">
                            <span className="text-gray-600">{status.code}:</span>
                            <span className="font-mono font-bold">
                              {typeof status.value === 'boolean' 
                                ? (status.value ? 'ON' : 'OFF')
                                : status.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune donnée disponible</p>
                    )}
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Colonne 2 : Contrôle & Stats */}
        <div className="space-y-6">
          {/* Contrôle manuel */}
          {details.switchId?.type === 'ir_controller' ? (
            <Card>
              <h2 className="text-xl font-bold mb-4">Contrôle Climatisation</h2>
              <AcControlPanel associationId={details._id} associationName={details.name} />
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Mode actuel</p>
                <p className="font-bold text-gray-800">
                  {details.autoMode ? '🤖 Automatique' : '🔧 Manuel'}
                </p>
              </div>
            </Card>
          ) : (
            <Card>
              <h2 className="text-xl font-bold mb-4">Contrôle manuel</h2>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleControl('on')}
                  className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-bold text-lg"
                >
                  🔆 ALLUMER
                </button>
                
                <button
                  onClick={() => handleControl('off')}
                  className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-bold text-lg"
                >
                  🌙 ÉTEINDRE
                </button>
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Mode actuel</p>
                <p className="font-bold text-gray-800">
                  {details.autoMode ? '🤖 Automatique' : '🔧 Manuel'}
                </p>
              </div>
            </Card>
          )}

          {/* Statistiques */}
          <Card>
            <h2 className="text-xl font-bold mb-4">Statistiques</h2>
            
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Total activations</p>
                <p className="text-2xl font-bold text-blue-600">
                  {details.statistics?.totalActivations || 0}
                </p>
              </div>

              {details.lastAction && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Dernière action</p>
                  <p className="font-bold text-gray-800 text-sm">
                    {details.lastAction.type.replace('_', ' ').toUpperCase()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(details.lastAction.timestamp).toLocaleString('fr-FR')}
                  </p>
                  {details.lastAction.triggeredBy && (
                    <p className="text-xs text-gray-600 mt-1">
                      Par: {details.lastAction.triggeredBy}
                    </p>
                  )}
                </div>
              )}

              {details.statistics?.lastPresenceDetected && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Dernière présence</p>
                  <p className="text-sm font-semibold text-gray-700">
                    {new Date(details.statistics.lastPresenceDetected).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Configuration */}
          {!editing && (
            <Card>
              <h2 className="text-xl font-bold mb-4">Configuration</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mode auto:</span>
                  <span className="font-bold">
                    {details.autoMode ? '✅ Activé' : '❌ Désactivé'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Programmation:</span>
                  <span className="font-bold">
                    {details.schedule?.enabled ? '✅ Activée' : '❌ Désactivée'}
                  </span>
                </div>

                {details.schedule?.enabled && (
                  <div className="space-y-1.5 pt-1">
                    {(details.schedulePeriods?.length > 0
                      ? details.schedulePeriods
                      : details.schedule.onTime && details.schedule.offTime
                        ? [{ name: 'Programmation', onTime: details.schedule.onTime, offTime: details.schedule.offTime }]
                        : []
                    ).map((p, i) => (
                      <div key={p._id || i} className="flex justify-between text-xs">
                        <span className="text-gray-500">{p.name}:</span>
                        <span className="font-mono font-bold">{p.onTime} → {p.offTime}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Délai extinction:</span>
                    <span className="font-bold">{details.settings?.presenceDelay}s</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-gray-600">Durée min ON:</span>
                    <span className="font-bold">{details.settings?.minOnDuration}s</span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssociationDetails;